import angular from "angular";

import { ApiValidationError, Onboarding } from "@salaxy/core";

import { OnboardingService, SessionService, SignatureService, WizardService, WizardStep } from "../../../services";
import { WizardController } from "../../bases/WizardController";

/**
 * Wizard for Creating a new Palkkaus.fi-account for a household.
 */
export class HouseholdOnboardingController extends WizardController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$scope", "WizardService", "OnboardingService", "SessionService", "SignatureService", "data"];

  /** Household wizard configuration */
  public wizardSteps: WizardStep[] = [
    {
      title: "Käyttäjän tiedot",
      heading: "Tervetuloa, Palkkamyllyyn!",
      intro: `Palkkamyllyn tarjoaa Rakennusliitto ry, joka toimii henkilötietojen käsittelijänä ja vastaa myös asiakaspalvelusta.\n\nPalvelun tuottajana, teknisenä toteuttajana ja myös henkilötietojen käsittelijä toimii Suomen Palkanlaskenta Oy (Palkkaus.fi).`,
      active: true,
      view: "salaxy-components/modals/onboarding/household/wizard_index.html",
      buttonsView: "salaxy-components/modals/onboarding/household/firstPageButtons.html",
    },
    /*
    {
      title: "Puhelinnumero",
      heading: "Puhelinnumero",
      intro: "Puhelinnumeroa tarvitsemme yhteydenpitoa varten. Jos rekisteröit Siirto-palvelussa olevan puhelinnumeron, voit maksaa ja vastaanottaa palkkoja mobiilimaksuilla. Vahvistamme antamasi numeron.",
      view: "salaxy-components/modals/onboarding/household/phoneNumber.html",
      buttonsView: "salaxy-components/modals/onboarding/household/phoneNumberButtons.html",
    },
    {
      title: "Puhelinnumeron vahvistaminen",
      heading: "Puhelinnumeron vahvistaminen",
      intro: "Olemme lähettäneet sinulle SMS-viestin varmistaaksemme numerosi. Syötä viestissä oleva vahvistuskoodi tähän.",
      view: "salaxy-components/modals/onboarding/household/phoneNumberVerify.html",
      buttonsView: "salaxy-components/modals/onboarding/household/phoneNumberVerifyButtons.html",
    },
    */
    {
      title: "Palkanmaksun tiedot",
      heading: "Palkanmaksun tiedot",
      view: "salaxy-components/modals/onboarding/household/household.html",
      buttonsView: "salaxy-components/modals/onboarding/household/householdButtons.html",
    },
    {
      title: "Palvelusopimus",
      heading: "Palvelusopimus ja valtuutus",
      view: "salaxy-components/modals/onboarding/household/contract.html",
      buttonsView: "salaxy-components/modals/onboarding/household/contractButtons.html",
    },
  ];

  /**
   * Field to expose forms validity state.
   */
  public formDataValidity: boolean;

  /** If true, step is proceeding */
  public isStepProceeding = false;

  public visibleSignatureMethods: object;

  constructor(
    $scope: angular.IScope,
    wizardService: WizardService,
    private onboardingService: OnboardingService,
    private sessionService: SessionService,
    private signatureService: SignatureService,
    data: any,
  ) {
    super($scope, wizardService, data);
  }

  /**
   * Implement IController
   */
  public $onInit() {
    this.wizardService.setSteps(this.wizardSteps);
    this.wizardService.activeStepNumber = 1;
    const signatureMethods = this.signatureService.getMethods("telia");

    this.visibleSignatureMethods = signatureMethods
      .filter(method => method.isPopular)
      .map(method => ({
        value: method.value,
        label: method.title,
        imgSrc: method.img,
      }));
  }

  /**
   * Navigates to the next step if possible and saves the data.
   */
  public goNext(): Promise<boolean> {
    if (this.isStepProceeding) {
      return Promise.resolve(false);
    }
    this.isStepProceeding = true;
    return this.save().then(() => {
      super.goNext();
      this.isStepProceeding = false;
      return true;
    }).catch((reason) => {
      this.isStepProceeding = false;
      return false;
    });
  }

  /** Returns true if user can go forward in wizard  */
  public get canGoNext(): boolean {
    if (this.steps.length > this.step) {
      if (this.steps[this.step] && !this.steps[this.step].disabled) {
        if (this.formDataValidity) {
          /* if (this.model.validation.isOfficialIdUnique == true){ */
          return true;
          /* } */
        }
      }
    }
    return false;
  }

  /** Runs a check function before going forward */

  public goNextIf(checkFunction: (goNext: () => Promise<boolean>) => void) {
    checkFunction(() => this.goNext());
  }

  /**
   * Navigates to the previous step if possible and saves the data.
   */
  public goPrevious() {
    if (this.isStepProceeding) {
      return;
    }
    this.isStepProceeding = true;
    this.save().then(() => {
      super.goPrevious();
      this.isStepProceeding = false;
    })
      .catch((reason) => {
        this.isStepProceeding = false;
      });
  }

  /**
   * Saves the data to server
   */
  public save(): Promise<Onboarding> {
    this.model.signature.email = this.model.person.contact.email;
    this.model.signature.telephone = this.model.person.contact.telephone;
    this.model.signature.personName = (this.model.person.firstName + " " + this.model.person.lastName).trim();
    return this.onboardingService.save();
  }

  /**
   * Returns signing url.
   */
  public get vismaSignUrl(): string {
    // HACK: Get rid of this: Use this.model.signature.digitalSignature.auth_service instead.
    const method = (this.model.signature as any).method;
    return this.onboardingService.getDigitalSignUrl(method);
  }

  /**
   * The onboarding model is provided by the onboarding service.
   */
  public get model(): Onboarding {
    return this.onboardingService.model;
  }

  /** Returns the PDF preview address for the authorization pdf. */
  public getPdfPreviewAddress() {
    return this.onboardingService.getPdfPreviewAddress();
  }

  /**
   * Returns validation error for key if exists.
   * @param key - Validation error key.
   */
  public getValidationError(key: string): ApiValidationError {
    if (this.model && this.model.validation) {
      return this.model.validation.errors.find((x) => x.key === key);
    }
    return null;
  }

  /**
   * Resets validation error for key if exists.
   * @param key - Validation error key.
   */
  public removeValidationError(key: string): void {
    if (this.model && this.model.validation) {
      (this.model.validation as any).errors = this.model.validation.errors.filter((x) => x.key !== key);
    }
  }

  /**
   * Launches the wizard.
   * @param id Optional onboarding id.
   */
  public launch(id: string = null): Promise<any> {
    return this.onboardingService.launchHouseholdOnboarding(id);
  }
}
